import { MetaFunction } from "@remix-run/node"
import {
  articleBodyStyle,
  articleImageStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  infoWrap,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import StickyHeader from "~/components/StickyHeader"
import Footer from "~/components/Footer"
import LinkButton from "~/components/LinkButton"
import Logo from "~/components/Logo"
import Link from "~/components/Link"
import { page } from "~/shared/styles.css"

// SEO FIX: Title shortened from 66 to 52 chars (was over 60 char limit)
const TITLE = "Features of Contemporary Gardens | Modern Design"
// SEO FIX: Description shortened from 188 to 152 chars (was over 155 char limit)
const DESCRIPTION =
  "Discover the key features of contemporary gardens: minimalist layouts, modern materials, sustainable elements, and outdoor living spaces."
const IMAGE =
  "https://rescapeai.com/how-to-design-a-contemporary-garden/third-ai-garden-design-with-stone-paving-area.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/garden-design-guide/contemporary/features-of-contemporary-gardens",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/garden-design-guide/contemporary/features-of-contemporary-gardens",
        },
        articleSection: ["Exploring Key Elements of Contemporary Garden Design"],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage:
            "url(/how-to-design-a-contemporary-garden/fourth-ai-garden-design-with-stone-paving-area.webp)",
          backgroundPosition: "20%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Features of Contemporary Gardens</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>
            Exploring the Key Elements That Define Modern Garden Design
          </h2>
          <p className={articleBodyStyle}>
            Contemporary gardens reflect modern lifestyles—prioritizing simplicity,
            functionality, and aesthetic appeal. Designed to complement current
            architectural styles, these gardens focus on clean lines, minimalism, and
            sustainable practices. Below are the key features that define contemporary
            garden design.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Minimalist Design and Clean Lines
            </h3>
            <img
              src={"/guide/geometry-in-contemporary-design.webp"}
              alt={"Geometric contemporary garden layout with clean lines"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Contemporary gardens often embrace simplicity, using clear geometric shapes
              and structured layouts. Linear pathways, rectangular lawns, and symmetric
              planting beds create a sense of order and calm. The emphasis is on reducing
              clutter, allowing each element to stand out.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Use of Modern Materials
            </h3>
            <img
              src={"/guide/steel-in-contemporary-design.webp"}
              alt={"Steel used as a structural element in contemporary garden design"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              <Link href="/garden-design-guide/contemporary/materials-and-plants">
                Materials like concrete, steel, glass, and composite decking
              </Link>{" "}
              are commonly used to craft pathways, retaining walls, and seating. These
              materials not only enhance the sleek, polished look but also offer
              durability and low maintenance. Smooth textures and neutral tones often
              dominate the palette.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Sustainable and Eco-Friendly Elements
            </h3>
            <p className={articleBodyStyle}>
              With growing awareness about the environment, sustainability plays a major
              role in contemporary gardens. Features may include:
            </p>
            <ul className={unorderedList}>
              <li>Rainwater harvesting systems</li>
              <li>Native or drought-tolerant plants</li>
              <li>Permeable paving to prevent runoff</li>
              <li>Solar-powered lighting</li>
            </ul>
            <p className={articleBodyStyle}>
              The goal is to create an eco-conscious outdoor space that supports
              biodiversity and reduces water usage.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Outdoor Living Spaces
            </h3>
            <img
              src={"/guide/fire-pit.webp"}
              alt={"Contemporary fire pit as part of an outdoor living space"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Contemporary gardens often act as extensions of indoor living areas.
              Functional outdoor spaces—such as lounges, dining zones, fire pits, and
              outdoor kitchens—are incorporated to encourage relaxation and socializing.
              Furniture tends to be modern, weather-resistant, and streamlined.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Structured Planting Schemes
            </h3>
            <img
              src={"/guide/architectural-plants.webp"}
              alt={"Architectural plants used in contemporary garden design"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Plants are selected for their architectural forms, textures, and year-round
              interest rather than floral abundance. Popular choices include ornamental
              grasses, succulents, bamboos, and shrubs with distinct shapes. Repetition of
              plant species creates rhythm and visual harmony.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Water Features with Simplicity
            </h3>
            <img
              src={"/guide/water-feature.webp"}
              alt={"Sleek water feature integrated into a contemporary garden design"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Water elements are designed to be sleek and tranquil rather than ornate.
              Reflective pools, slim waterfalls, and calm fountains add a soothing
              ambiance. These features are typically integrated seamlessly into the
              overall design without overpowering the space.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Neutral and Monochromatic Color Schemes
            </h3>
            <p className={articleBodyStyle}>
              The color palette in contemporary gardens is often restrained, focusing on
              shades of green complemented by whites, greys, or blacks. Pops of color,
              whether from vibrant cushions or seasonal plants, are used sparingly for
              contrast.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Low Maintenance
            </h3>
            <p className={articleBodyStyle}>
              Designed for modern busy lifestyles, contemporary gardens prioritize ease of
              care. The use of mulch, automatic irrigation systems, and hardy plants
              minimizes upkeep while keeping the garden visually appealing.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
              id="contemporary-lighting"
            >
              Integrated Lighting
            </h3>
            <img
              src={"/guide/SolarPoweredGardenSpotlight.webp"}
              alt={"Solar powered garden spotlight for contemporary lighting design"}
              className={articleImageStyle}
              loading="lazy"
            />
            <p className={articleBodyStyle}>
              Subtle, strategically placed lighting plays a vital role in contemporary
              garden design. LED strip lights along pathways, up-lighting on trees, and
              accent lighting for water features enhance the atmosphere and ensure
              usability at night.
            </p>
          </div>
          <div className={infoWrap}>
            <h3
              className={h3Style}
              style={{ marginTop: "1.25rem", marginBottom: ".8rem" }}
            >
              Blend of Nature and Architecture
            </h3>
            <p className={articleBodyStyle}>
              Contemporary gardens strike a harmonious balance between man-made elements
              and natural features. The garden is often designed to flow seamlessly with
              the architecture of the home, creating a unified and cohesive look.
            </p>
          </div>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Conclusion</h2>
          <p className={articleBodyStyle}>
            Contemporary gardens are a reflection of modern aesthetics and environmental
            awareness. With an emphasis on simplicity, functionality, and sustainability,
            they offer serene outdoor spaces that are both beautiful and practical.
            Whether in urban courtyards or suburban landscapes, these gardens offer a
            stylish retreat that fits perfectly into the rhythm of contemporary living.
          </p>
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup">
            Create your contemporary garden here
          </LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
